import Icon from '@shelflix/ui-web/components/Icon';
import { Badge } from '@shelflix/ui-web/components/ui/badge';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Command, CommandEmpty, CommandItem, CommandList } from '@shelflix/ui-web/components/ui/command';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { useEffect, useState } from 'react';

type Value = { text: string; value: unknown };

interface MultiSelectProps {
  onSearch?: (search: string) => unknown;
  items: Value[];
  onSelect?: (value: Value) => unknown;
  onRemove?: (value: Value) => unknown;
  initialValue?: Value[];
  horizontal?: boolean;
}

export function MultiSelect({
  initialValue,
  items,
  onRemove,
  onSelect,
  onSearch,
  horizontal = false,
}: MultiSelectProps) {
  const [search, setSearch] = useState('');
  const [showList, setShowList] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [currentValue, setCurrentValue] = useState<Value[]>(initialValue ?? []);
  const [selectedValue, setSelectedValue] = useState<Value | null>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    if (onSearch) onSearch(debouncedSearch);
  }, [debouncedSearch]);

  const handleSelect = (value: Value) => {
    setCurrentValue([...currentValue, ...[value]]);
    setSelectedValue(null);
    if (onSelect) {
      onSelect(value);
    }
    setSearch('');
    setShowList(false);
  };

  const handleRemove = (value: Value) => {
    if (onRemove) {
      onRemove(value);
    }
    setCurrentValue(currentValue.filter((i) => i.value !== value.value));
    setShowList(false);
  };

  return (
    <div className={`flex w-full ${horizontal ? 'align-center flex-row gap-6' : 'flex-col'}`}>
      <div className="flex-gow-0 relative flex w-full gap-[15px]">
        <Input
          className="sm:w-[416px]"
          placeholder="Digite para buscar"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setShowList(true);
          }}
          onFocus={() => setShowList(true)}
          onBlur={() => setTimeout(() => setShowList(false), 150)}
        />

        {showList && (
          <div className="absolute z-10 mt-1 w-full rounded-md border shadow-md">
            <Command>
              <CommandList className="max-h-[200px] overflow-auto">
                {items.length > 0 ? (
                  items.map((item) => (
                    <CommandItem
                      key={`${item.value}`}
                      onSelect={() => {
                        setSelectedValue(item);
                        setSearch(item.text);
                      }}
                      className="cursor-pointer"
                    >
                      <div>
                        <div className="text-sm font-medium">{item.text}</div>
                      </div>
                    </CommandItem>
                  ))
                ) : debouncedSearch ? (
                  <CommandEmpty>Nenhum item encontrado</CommandEmpty>
                ) : (
                  <CommandEmpty>Digite para buscar</CommandEmpty>
                )}
              </CommandList>
            </Command>
          </div>
        )}
        <Button
          type="button"
          size="icon"
          className="bg-primary hover:bg-primary h-9 w-9 cursor-pointer text-white"
          onClick={() => selectedValue && handleSelect(selectedValue)}
        >
          <Icon name="add" color="#fff" />
        </Button>
      </div>
      <div className={`${horizontal ? 'w-full' : 'mt-4'} flex flex-wrap gap-4`}>
        {currentValue?.map((item, idx) => (
          <Badge
            key={`badge-${idx}`}
            className="bg-secondary text-primary cursor-pointer px-4 py-2"
            onClick={() => handleRemove(item)}
          >
            <span className="mr-2">{item.text}</span>
            <Icon name="close" size={16} />
          </Badge>
        ))}
      </div>
    </div>
  );
}
